import { apiClient } from './apiClient';

export type EssentialsStage = 'lead' | 'prospect' | 'client';

export interface EssentialsKpis {
  new_leads_week: number;
  active_prospects: number;
  customers: number;
  follow_ups_due: number;
}

export interface DashboardContactRow {
  id: string;
  full_name: string;
  company_name: string;
  source: string;
  stage: string;
  created_at: string | null;
  next_follow_up_at: string | null;
  assigned_to_name: string | null;
  overdue?: boolean;
}

export interface WebsiteActivityRow {
  kind: 'chat' | 'form';
  type: 'Chat' | 'Form';
  name: string;
  timestamp: string | null;
  session_id?: string;
  contact_id?: string;
}

export interface EssentialsDashboard {
  kpis: EssentialsKpis;
  recent_leads: DashboardContactRow[];
  follow_ups_due: DashboardContactRow[];
  recent_website_activity: WebsiteActivityRow[];
  leads_this_month: { count: number; previous: number };
}

export interface ContactFilters {
  search?: string;
  source?: string;
  assigned_to?: string;
  follow_up?: 'due';
}

export interface InboxItem {
  kind: 'chat' | 'form';
  type: 'Website Chat' | 'Website Form';
  name: string;
  timestamp: string | null;
  session_id?: string;
  contact_id?: string | null;
  status?: string;
  stage?: string;
  assigned_user?: { id: string; email: string } | null;
}

class EssentialsService {
  async getDashboard(): Promise<EssentialsDashboard> {
    return apiClient.get('/essentials/dashboard/');
  }

  /** Aggregate inbox: Website Chat + Website Form, tenant-scoped (backend). */
  async getInbox(): Promise<{ results: InboxItem[]; sources: string[] }> {
    return apiClient.get('/chatbot/manage/inbox/essentials/');
  }

  /** Build the CSV export URL for the current filter (tenant scoped server-side). */
  exportUrl(stage: string, filters: ContactFilters = {}): string {
    const params = new URLSearchParams();
    if (stage) params.set('stage', stage);
    if (filters.search) params.set('search', filters.search);
    if (filters.source) params.set('source', filters.source);
    if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
    if (filters.follow_up === 'due') params.set('follow_up', 'due');
    return `/essentials/contacts/export/?${params.toString()}`;
  }

  async exportCsv(stage: string, filters: ContactFilters = {}): Promise<Blob> {
    return apiClient.fetchAuthorizedBlob(this.exportUrl(stage, filters));
  }
}

export const essentialsService = new EssentialsService();
